import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
} from "@mui/material";
import {
  styled,
  keyframes,
} from "@mui/material/styles";
import CustomGlowButton from "../GlowingButton";
import { useNavigate } from "react-router-dom";

const FeatureBox = styled(Box)(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: "14px",
  marginBottom: theme.spacing(2),
  transition: "all 0.35s ease",
  cursor: "pointer",
  border: active
    ? "1px solid rgba(99,102,241,0.4)"
    : "1px solid rgba(255,255,255,0.06)",
  background: active
    ? "rgba(99,102,241,0.12)"
    : "rgba(255,255,255,0.02)",

  "&:hover": {
    background: "rgba(255,255,255,0.06)",
    transform: "translateX(5px)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: "44px",
  height: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  background: "rgba(99,102,241,0.14)",
  flexShrink: 0,
}));

const GlassContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: "28px",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 10px 40px rgba(0,0,0,0.25)",

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(2),
  },
}));

const ImageContainer = styled(Box)(() => ({
  width: "100%",
  position: "relative",
  borderRadius: "24px",
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.08)",

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

const blink = keyframes`
  0%,100%{
    opacity:1;
  }
  50%{
    opacity:0;
  }
`;

const GlassBox = ({
  index,
  title,
  subtitle,
  features,
}) => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] =
    useState(0);

  const [displayText, setDisplayText] =
    useState("");

  const [showImage, setShowImage] =
    useState(false);

  useEffect(() => {
    const text =
      features?.[activeFeature]?.text || "";

    setDisplayText("");
    setShowImage(false);

    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      setDisplayText(
        text.substring(0, currentIndex + 1)
      );

      currentIndex++;

      if (currentIndex > text.length) {
        clearInterval(typingInterval);

        setTimeout(() => {
          setShowImage(true);
        }, 800);
      }
    }, 90);

    return () =>
      clearInterval(typingInterval);
  }, [activeFeature, features]);

  const productRoutes = [
  "/products/avalanche",
  "/products/one",
  "/products/visva",
  "/products/aurora",
  "/products/netzero",
];

  return (
    <GlassContainer>
      <Typography
        variant="h4"
        sx={{
          color: "#fff",
          fontWeight: 700,
          mb: 2,
          fontSize: {
            xs: "28px",
            md: "36px",
          },
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: "#8C8CC2",
          mb: 5,
          fontSize: {
            xs: "14px",
            md: "16px",
          },
          lineHeight: 1.8,
          maxWidth: "900px",
        }}
      >
        {subtitle}
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          {features?.map((feature, idx) => (
            <FeatureBox
              key={idx}
              active={activeFeature === idx}
              onClick={() =>
                setActiveFeature(idx)
              }
            >
              <IconWrapper>
                {feature?.icon}
              </IconWrapper>

              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: {
                      xs: "14px",
                      md: "16px",
                    },
                  }}
                >
                  {feature?.title}
                </Typography>
              </Box>
            </FeatureBox>
          ))}

          <Box mt={4}>
            <CustomGlowButton
              istransform={true}
              text={`Explore ${index === 0
                ? "AVALANCHE"
                : index === 1
                  ? "ONE"
                  : index === 2
                    ? "VISVA"
                    : index === 3
                      ? "AURORA"
                      : "NETZERO"
                }`}
              fw={1}
              onClick={() => navigate(productRoutes[index] || "/products/netzero")}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "16px",
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.96), rgba(31,41,55,0.88))",
              border:
                "1px solid rgba(255,255,255,0.06)",
              minHeight: {
                xs: "300px",
                md: "450px",
              },
              p: {
                xs: 1,
                md: 2,
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,

                p: {
                  xs: 3,
                  md: 5,
                },

                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",

                opacity: showImage ? 0 : 1,

                transition: "opacity 0.8s ease",

                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  width: "100%",
                  color: "transparent",
                  WebkitTextStroke:
                    "0.5px #FFFFFF",

                  fontWeight: 500,
                  lineHeight: 1.2,
                  fontSize: {
                    xs: "24px",
                    md: "60px",
                  },
                  textAlign: "left",
                  whiteSpace: "wrap",
                  overflowWrap: "break-word",
                  textTransform: "none",
                }}
              >
                {displayText}

                {!showImage && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "3px",
                      height: "1em",
                      bgcolor: "#fff",
                      ml: "4px",
                      verticalAlign: "middle",
                      animation: `${blink} 1s infinite`,
                    }}
                  />
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,

                opacity: showImage ? 1 : 0,

                transform: showImage
                  ? "scale(1)"
                  : "scale(1.12)",

                filter: showImage
                  ? "blur(0px)"
                  : "blur(12px)",

                transition:
                  "opacity 1.4s ease, transform 1.8s ease, filter 1.8s ease",
              }}
            >
              <ImageContainer>
                <Box
                  component="img"
                  src={
                    features?.[
                      activeFeature
                    ]?.mainImage
                  }
                  alt="Feature"
                  sx={{
                    width: "100%",
                    height: {
                      xs: "260px",
                      md: "420px",
                    },
                    objectFit: "contain",
                  }}
                />
              </ImageContainer>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </GlassContainer>
  );
};

export default GlassBox;